import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, filter, first, map, Observable, of, switchMap } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { ConnectorInfo } from '@blaze-shared/api-contracts-domain';
import { accountSelectors, ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { OAuthService } from '@blaze-shared/auth-data-access';
import {
  ConnectorsApiService,
  ConnectorsCollectionService,
  GoogleApiService,
} from '@blaze-shared/connectors-data-access';
import {
  AuthorizeUrlSpecification,
  ConnectorConfiguration,
  ConnectorOAuthConfig,
  GenericOAuthAuthorizationMessage,
} from '@blaze-shared/connectors-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { mapObjectsToOptions, SelectOption } from '@blaze-shared/shared-util';

type ConnectionSelectorOAuthServiceName = 'google';

@Injectable()
export class ConnectionSelectorService {
  private readonly connectorsApiService = inject(ConnectorsApiService);
  private readonly connectorsCollService = inject(ConnectorsCollectionService);
  private readonly googleApiService = inject(GoogleApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly oAuthService = inject(OAuthService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly store = inject(Store);

  private authState = '';
  private config: ConnectorOAuthConfig;

  connectOAuth(
    serviceName: ConnectionSelectorOAuthServiceName,
    specification: AuthorizeUrlSpecification,
  ): Observable<GenericOAuthAuthorizationMessage> {
    return this.connectorsApiService.getServiceAuthorizeUrl(serviceName, specification).pipe(
      map(({ url }) => {
        const authUrl = this.prepareAuthorizationUrl(url, this.authState);
        if (!authUrl) {
          throw new Error('Could not find authorization url');
        }
        return authUrl;
      }),
      switchMap((authUrl: string) => {
        return this.oAuthService.openPopup(authUrl, {
          width: '700px',
          height: '500px',
        });
      }),
      switchMap((ref) =>
        this.oAuthService.getAuthorizationMessage<GenericOAuthAuthorizationMessage>(ref),
      ),
      map((message) => this.filterOAuthMessage(message, this.config)),
      switchMap((message) => {
        return this.connectorsApiService.postInstallServiceApp(serviceName, message).pipe(
          map(() => {
            return message;
          }),
        );
      }),
      first(),
    );
  }

  createGoogleCalendarConnector(
    authState: string,
    calendarId: string,
    calendarName: string,
    templateId: string,
  ): Observable<ConnectorInfo | null> {
    return combineLatest({
      accountId: this.store.select(accountSelectors.selectAccountId),
      projectId: this.store.select(runtimeSelectors.selectProjectId),
      userId: this.projectContextFacade.userId$,
    }).pipe(
      first(),
      map(({ accountId, projectId, userId }) => {
        if (!userId) {
          console.error('Required external user ID to connect Google Calendar.');
          return;
        }
        const config = this.createConnectorConfig({
          accountId,
          authState,
          calendarId,
          calendarName,
          connectorName: `Calendar (${calendarId} - ${userId})`,
          externalAccountRowId: null,
          projectId,
          templateId,
          userId,
        });
        return config;
      }),
      filter((config) => !!config),
      switchMap((config) => {
        return this.connectorsCollService.add(config).pipe(
          catchError((error) => {
            this.notificationService.showError(error);
            return of(null);
          }),
        );
      }),
    );
  }

  createGoogleMailConnector(
    authState: string,
    templateId: string,
  ): Observable<ConnectorInfo | null> {
    return combineLatest({
      accountId: this.store.select(accountSelectors.selectAccountId),
      projectId: this.store.select(runtimeSelectors.selectProjectId),
      userId: this.projectContextFacade.userId$,
      varUser: this.projectContextFacade.varUser$,
    }).pipe(
      first(),
      map(({ accountId, projectId, userId, varUser }) => {
        if (!userId) {
          console.error('User ID is required to connect Google Mail.');
          return;
        }

        const config = this.createConnectorConfig({
          accountId,
          authState,
          connectorName: `GMail (${varUser?.Email || userId})`,
          email: varUser?.Email,
          externalAccountRowId: null,
          projectId,
          templateId,
          userId,
        });
        return config;
      }),
      filter((config) => !!config),
      switchMap((config) => {
        return this.connectorsCollService.add(config).pipe(
          catchError((error) => {
            this.notificationService.showError(error);
            return of(null);
          }),
        );
      }),
    );
  }

  /**
   * Loads a list of calendars and creates dropdown options from them.
   */
  loadCalendars(authState: string): Observable<SelectOption<string>[]> {
    if (!authState) {
      return of([]);
    }
    return this.googleApiService.getCalendars(authState).pipe(
      catchError((error) => {
        this.notificationService.showError(error);
        return of([]);
      }),
      map((calendars) => mapObjectsToOptions(calendars, 'id', 'summary') ?? []),
    );
  }

  /**
   * Creates configuration for a connector.
   */
  private createConnectorConfig(configData: {
    accountId: string;
    authState: string;
    calendarId?: string;
    calendarName?: string;
    connectorName: string;
    email?: string;
    externalAccountRowId: string | null;
    projectId: string;
    templateId: string;
    userId: string | null;
  }): ConnectorConfiguration {
    const {
      accountId,
      authState,
      calendarId,
      calendarName,
      connectorName,
      email,
      externalAccountRowId,
      projectId,
      templateId,
      userId,
    } = configData;

    const configuration: {
      authState: string;
      calendarId?: string;
      calendarName?: string;
      email?: string;
      isUserScoped: boolean;
      name: string;
    } = {
      authState,
      isUserScoped: !!userId,
      name: connectorName,
    };
    if (calendarId) {
      configuration.calendarId = calendarId;
    }
    if (calendarName) {
      configuration.calendarName = calendarName;
    }
    if (email) {
      configuration.email = email;
    }

    const connectorConfig: ConnectorConfiguration = {
      accountId: accountId || '',
      actions: [],
      configuration,
      datasets: [],
      externalAccountRowId: externalAccountRowId ?? undefined,
      name: connectorName,
      projectId,
      templateId: templateId,
      userId: userId ?? undefined,
    };

    return connectorConfig;
  }

  private filterOAuthMessage(
    message: GenericOAuthAuthorizationMessage,
    config?: ConnectorOAuthConfig,
  ): GenericOAuthAuthorizationMessage {
    const filteredMessage: GenericOAuthAuthorizationMessage = {
      code: message.code,
      state: message.state,
    };

    if (config?.allowedInstallFields) {
      config.allowedInstallFields.forEach((key) => {
        if (message[key]) {
          filteredMessage[key] = message[key];
        }
      });
    }

    return filteredMessage;
  }

  private prepareAuthorizationUrl(url: string, authState: string): string {
    if (!authState) {
      return url;
    }

    const ourl = new URL(url);
    ourl.searchParams.set('state', authState);
    return ourl.toString();
  }
}
